import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  TextField,
  useMediaQuery,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { FaClock, FaFileAlt, FaMoneyCheck, FaUserSlash, FaEnvelope } from "react-icons/fa";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { keyframes } from "@emotion/react";
import EmailSendingAnimation from './extras/EmailSendingAnimation.js';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from "../context/AuthContext";
const API_URL =
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_API_URL_DEVELOPMENT
    : process.env.REACT_APP_API_URL;

const iconMap = {
  FaClock: <FaClock size={32} color="#276a78" />,
  FaFileAlt: <FaFileAlt size={32} color="#276a78" />,
  FaMoneyCheck: <FaMoneyCheck size={32} color="#276a78" />,
  FaUserSlash: <FaUserSlash size={32} color="#276a78" />,
  FaEnvelope: <FaEnvelope size={32} color="#276a78" />,
};


const LettersPage = () => {
  const { employeeName } = useContext(AuthContext);
  const [letters, setLetters] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [actionsTaken, setActionsTaken] = useState("");
  const [templateText, setTemplateText] = useState("");
  const [editableTemplate, setEditableTemplate] = useState("");
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loadingLetters, setLoadingLetters] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [error, setError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);

  // Fetch letters
  useEffect(() => {
    const fetchLetters = async () => {
      try {
        const token = localStorage.getItem("token");
        console.log("Fetching letters with token:", token ? "Token exists" : "No token");
        console.log("API URL:", `${API_URL}/letters`);
        
        const response = await fetch(`${API_URL}/letters`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
  
        const data = await response.json();
        console.log("Fetched letters:", data); // ✅ Debugging step
        console.log("Response status:", response.status, response.ok);
  
        if (response.ok) {
          setLetters(Array.isArray(data) ? data : []);
        } else {
          console.error("Error response:", data);
          setError(data.error || "Failed to load letters.");
          setLetters([]);
        }
        setLoadingLetters(false);
      } catch (err) {
        console.error("Error fetching letters - Full error:", err);
        console.error("Error message:", err.message);
        console.error("Error stack:", err.stack);
        setError(`Failed to load letters: ${err.message}`);
        setLoadingLetters(false);
      }
    };
  
    console.log("Letters useEffect running, about to fetch letters");
    fetchLetters();
  }, []);

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/locations`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        if (response.ok) {
          setLocations(Array.isArray(data) ? data : []);
        } else {
          console.error("Error fetching locations:", data);
          setError(data.error || "Failed to load locations.");
          setLocations([]);
        }
        setLoadingLocations(false);
      } catch (err) {
        console.error("Error fetching locations:", err);
        setError("Failed to load locations.");
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);

  // Fetch employees when location changes
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!selectedLocation) {
        setEmployees([]);
        return;
      }

      setLoadingEmployees(true);
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_URL}/locations/${selectedLocation}/employees`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        if (response.ok) {
          setEmployees(Array.isArray(data) ? data : []);
        } else {
          console.error("Error fetching employees:", data);
          setError(data.error || "Failed to load employees");
          setEmployees([]);
        }
      } catch (err) {
        console.error("Error:", err);
        setError("Failed to load employees");
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchEmployees();
  }, [selectedLocation]);

  const updateTemplateText = (location, employee, date = selectedDate) => {
    if (!selectedLetter) return;
  
    let updatedTemplate = selectedLetter.template;
  
    if (employee) {
      updatedTemplate = updatedTemplate.replaceAll(
        "[Employee Name]",
        `${employee.firstName} ${employee.lastName}`
      );
    }
  
    if (location) {
      const locationName = locations.find((loc) => loc.id === location)?.name;
      updatedTemplate = updatedTemplate.replaceAll(
        "[Location Name]",
        locationName || ""
      );
    }
  
    // Replace multiple date variations dynamically
    const formattedDate = date ? date.format("MM/DD/YYYY") : dayjs().format("MM/DD/YYYY");
    updatedTemplate = updatedTemplate
      .replaceAll("[Date]", formattedDate)
      .replaceAll("[Date(s)]", formattedDate)
      .replaceAll("[Termination Date]", formattedDate);
  
    if (description) {
      updatedTemplate = updatedTemplate.replaceAll("[Description]", description);
    }
  
    if (actionsTaken) {
      updatedTemplate = updatedTemplate.replaceAll("[Actions Taken]", actionsTaken);
    }
    
    // Replace [Manager Name] with the current user's name
    if (employeeName) {
      updatedTemplate = updatedTemplate.replaceAll("[Manager Name]", employeeName);
    }
  
    setTemplateText(updatedTemplate);
    setEditableTemplate(updatedTemplate);
  };
  
  

  const handleLocationChange = (event) => {
    const locationId = event.target.value;
    setSelectedLocation(locationId);
    setSelectedEmployee(null);
    updateTemplateText(locationId, null);
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    updateTemplateText(selectedLocation, employee);
  };

  const handleOpenModal = (letter) => {
    setSelectedLetter(letter);
    setSelectedDate(dayjs()); // Default to today
    setTemplateText(letter.template);
    setEditableTemplate(letter.template);
    updateTemplateText(selectedLocation, selectedEmployee, dayjs()); // Ensure date updates
    setIsModalOpen(true);
  };
  

  const handleCloseModal = () => {
    setSelectedLetter(null);
    setIsModalOpen(false);
    setSelectedEmployee(null);
    setDescription("");
    setActionsTaken("");
    setTemplateText("");
    setEditableTemplate("");
    setSelectedLocation("");
    setSelectedDate(dayjs());
  };
  const handleSendEmail = async () => {
    if (!selectedEmployee || !selectedLetter || !editableTemplate) {
      setError('Please fill in all required fields.');
      return;
    }
  
    setIsSending(true);
    setShowAnimation(true);
    
    const animationStart = Date.now();
    const MINIMUM_ANIMATION_DURATION = 2000;
  
    try {
      const token = localStorage.getItem('token');

      // Check if this is a termination letter by type or slug
      if (selectedLetter.type === 'TERMINATION' || selectedLetter.type === 'termination') {
        console.log('Processing termination letter...');

        // First send the termination request
        const terminationResponse = await fetch(`${API_URL}/employees/terminate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            employeeId: selectedEmployee.id,
            terminationDate: selectedDate.format('YYYY-MM-DD'),
            reason: editableTemplate.split('Reason for termination:')[1]?.split('Please note:')[0]?.trim() || 'Not specified',
            letterId: selectedLetter.id
          })
        });

        console.log('Termination request sent:', {
          employeeId: selectedEmployee.id,
          terminationDate: selectedDate.format('YYYY-MM-DD')
        });

        if (!terminationResponse.ok) {
          const errorData = await terminationResponse.json();
          throw new Error(errorData.message || 'Failed to process termination');
        }

        const terminationData = await terminationResponse.json();
        console.log('Termination successful:', terminationData);

      } else {
        // For non-termination letters, use the regular send endpoint
        const letterResponse = await fetch(`${API_URL}/letters/send`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            employeeId: selectedEmployee.id,
            letterId: selectedLetter.id,
            letter: editableTemplate,
            type: selectedLetter.type,
            sendEmail: true,
            employeeEmail: selectedEmployee.email
          })
        });

        if (!letterResponse.ok) {
          const errorData = await letterResponse.json();
          throw new Error(errorData.error || 'Failed to process letter');
        }
      }
      
      // Ensure minimum animation duration
      const elapsed = Date.now() - animationStart;
      if (elapsed < MINIMUM_ANIMATION_DURATION) {
        await new Promise(resolve => setTimeout(resolve, MINIMUM_ANIMATION_DURATION - elapsed));
      }
  
      alert(selectedLetter.type.toLowerCase() === 'termination' ? 
        'Termination processed successfully!' : 
        'Letter sent successfully!');
      handleCloseModal();
    } catch (err) {
      console.error('Error processing letter:', err);
      setError(err.message || 'Failed to process letter');
    } finally {
      setIsSending(false);
      setShowAnimation(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          width: '100%',
          padding: isMobile ? 1 : 3,
          backgroundColor: '#f9fafb',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: '16px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
            backgroundColor: '#fff',
            overflow: 'hidden',
            mb: 3,
            maxWidth: "1400px",
            mx: "auto",
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'center' }}>
            <FaFileAlt size={28} color="#276a78" style={{ marginRight: '12px' }} />
            <Typography
              variant="h4"
              sx={{ 
                fontWeight: 600, 
                color: '#276a78', 
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}
            >
              Prepared Letters
            </Typography>
          </Box>

          {/* Instructions Panel */}
          <Box 
            sx={{ 
              p: 2, 
              mb: 4, 
              bgcolor: 'rgba(39, 106, 120, 0.05)',
              border: '1px solid rgba(39, 106, 120, 0.1)',
              borderRadius: '8px'
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, color: '#276a78', fontWeight: 500 }}>
              How to use the Letters page:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              1. Select a letter template from the options below
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              2. Fill in the required information (location, employee, date, etc.)
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              3. Preview the generated letter on the right side of the dialog
            </Typography>
            <Typography variant="body2">
              4. Send the letter via email or download it as needed
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}
            >
              {error}
            </Alert>
          )}

        {/* Letters Display - List for Desktop, Cards for Mobile */}
        {loadingLetters ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress sx={{ color: '#276a78' }} />
          </Box>
        ) : letters.length === 0 ? (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 4, 
              px: 2, 
              bgcolor: 'rgba(39, 106, 120, 0.03)',
              borderRadius: '12px',
              border: '1px dashed rgba(39, 106, 120, 0.2)'
            }}
          >
            <FaFileAlt size={32} color="#276a78" style={{ marginBottom: '16px', opacity: 0.5 }} />
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#276a78', 
                fontWeight: 500, 
                mb: 1
              }}
            >
              No letters found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              There are no letter templates available at the moment.
            </Typography>
          </Box>
        ) : isMobile ? (
          // Mobile Card View
          <Grid container spacing={3}>
            {letters.map((letter) => (
              <Grid item xs={12} sm={6} key={letter.id}>
                <Card
                  sx={{
                    p: 3,
                    bgcolor: "#ffffff",
                    height: "200px",
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(39, 106, 120, 0.1)',
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: '0 8px 16px rgba(39, 106, 120, 0.15)',
                      bgcolor: 'rgba(39, 106, 120, 0.02)',
                    },
                    cursor: "pointer",
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                  onClick={() => handleOpenModal(letter)}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      padding: 0,
                      "&:last-child": {
                        paddingBottom: 0
                      }
                    }}
                  >
                    <Box 
                      sx={{ 
                        mb: 2,
                        backgroundColor: 'rgba(39, 106, 120, 0.08)',
                        borderRadius: '50%',
                        width: '64px',
                        height: '64px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {iconMap[letter.icon] || (
                        <FaFileAlt size={32} color="#276a78" />
                      )}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ 
                        fontWeight: 600, 
                        color: '#2d3748', 
                        mb: 1,
                        fontFamily: 'Poppins'
                      }}
                    >
                      {letter.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{
                        color: '#718096',
                        maxWidth: '80%',
                        mx: 'auto'
                      }}
                    >
                      {letter.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          // Desktop List View
          <Paper 
            sx={{ 
              borderRadius: '12px',
              overflow: 'hidden',
              border: '1px solid rgba(39, 106, 120, 0.1)',
            }}
          >
            <List sx={{ p: 0 }}>
              {letters.map((letter, index) => (
                <React.Fragment key={letter.id}>
                  <ListItemButton
                    onClick={() => handleOpenModal(letter)}
                    sx={{
                      py: 3,
                      px: 4,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'rgba(39, 106, 120, 0.03)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 'auto',
                        mr: 3,
                      }}
                    >
                      <Box 
                        sx={{ 
                          backgroundColor: 'rgba(39, 106, 120, 0.08)',
                          borderRadius: '12px',
                          width: '56px',
                          height: '56px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {iconMap[letter.icon] || (
                          <FaFileAlt size={28} color="#276a78" />
                        )}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 600, 
                            color: '#2d3748',
                            mb: 0.5,
                            fontFamily: 'Poppins'
                          }}
                        >
                          {letter.name}
                        </Typography>
                      }
                      secondary={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#718096',
                            lineHeight: 1.6
                          }}
                        >
                          {letter.description}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                  {index < letters.length - 1 && (
                    <Divider sx={{ mx: 4 }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}

         {/* Letter Dialog */}
         <Dialog
  open={isModalOpen}
  onClose={handleCloseModal}
  fullScreen={isMobile}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      height: isMobile ? '100vh' : 'auto',
      maxHeight: isMobile ? '100vh' : '85vh',
      width: isMobile ? '100vw' : '80vw',
      margin: isMobile ? 0 : 'auto',
      borderRadius: isMobile ? 0 : '16px',
      overflow: 'hidden',
      position: 'relative',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    },
  }}
>
          <DialogTitle
            sx={{
              bgcolor: '#276a78',
              color: 'white',
              py: 2,
              fontFamily: 'Poppins',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '1rem', md: '1.25rem' }
                }}
              >
                {selectedLetter?.name}
              </Typography>
              <IconButton
                aria-label="close"
                onClick={handleCloseModal}
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.85)',
                  '&:hover': {
                    color: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.08)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.05)' }} />
          
          <DialogContent sx={{ p: { xs: 2, md: 4 }, position: 'relative' }}>
          <Grid container spacing={3}>
              {/* Left Column - Form Fields */}
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    p: { xs: 2, md: 3 },
                    bgcolor: 'rgba(39, 106, 120, 0.03)',
                    borderRadius: '12px',
                    mb: 3,
                    border: '1px solid rgba(39, 106, 120, 0.1)'
                  }}
                >
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      mb: 1, 
                      color: '#276a78',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <FaFileAlt size={16} />
                    Letter Details
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Select a location and employee, then customize the letter's date as needed.
                    All fields are required to generate the complete letter.
                  </Typography>
                  
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={selectedLocation}
                      onChange={handleLocationChange}
                      label="Location"
                      disabled={loadingLocations}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            overflow: 'auto'
                          }
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(39, 106, 120, 0.2)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(39, 106, 120, 0.5)',
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select Location
                      </MenuItem>
                      {locations.map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          <Box>
                            <Typography>{location.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {location.employeeCount || 0} employees
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Employee</InputLabel>
                    <Select
                      value={selectedEmployee || ""}
                      onChange={(e) => handleEmployeeSelect(e.target.value)}
                      label="Employee"
                      disabled={!selectedLocation || loadingEmployees}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            overflow: 'auto'
                          }
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(39, 106, 120, 0.2)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(39, 106, 120, 0.5)',
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        {!selectedLocation
                          ? "Select a location first"
                          : loadingEmployees
                          ? "Loading employees..."
                          : "Select Employee"}
                      </MenuItem>
                      {employees.map((employee) => (
                        <MenuItem key={employee.id} value={employee}>
                          <Box>
                            <Typography>
                              {employee.firstName} {employee.lastName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {employee.position}{" "}
                              {employee.userRole ? `(${employee.userRole})` : ""}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <DatePicker
                    label="Date"
                    value={selectedDate || dayjs()}
                    onChange={(newValue) => {
                      setSelectedDate(newValue);
                      updateTemplateText(selectedLocation, selectedEmployee, newValue);
                    }}
                    sx={{ 
                      width: "100%", 
                      mb: 3,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(39, 106, 120, 0.2)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(39, 106, 120, 0.5)',
                      },
                    }}
                  />
                </Box>

                {(selectedLetter?.template.includes("[Description]") || selectedLetter?.template.includes("[Actions Taken]")) && (
                  <Box
                    sx={{
                      p: { xs: 2, md: 3 },
                      bgcolor: 'rgba(39, 106, 120, 0.03)',
                      borderRadius: '12px',
                      border: '1px solid rgba(39, 106, 120, 0.1)'
                    }}
                  >
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        mb: 1, 
                        color: '#276a78',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <FaFileAlt size={16} />
                      Additional Information
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      This section appears for specific letter types that require more details.
                      The information you enter here will be incorporated into the letter text.
                    </Typography>

                    {selectedLetter?.template.includes("[Description]") && (
                      <TextField
                        label="Description"
                        multiline
                        rows={3}
                        fullWidth
                        value={description}
                        onChange={(e) => {
                          setDescription(e.target.value);
                          updateTemplateText(selectedLocation, selectedEmployee);
                        }}
                        sx={{ 
                          mb: 3,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(39, 106, 120, 0.2)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(39, 106, 120, 0.5)',
                          },
                        }}
                      />
                    )}

                    {selectedLetter?.template.includes("[Actions Taken]") && (
                      <TextField
                        label="Actions Taken"
                        multiline
                        rows={3}
                        fullWidth
                        value={actionsTaken}
                        onChange={(e) => {
                          setActionsTaken(e.target.value);
                          updateTemplateText(selectedLocation, selectedEmployee);
                        }}
                        sx={{ 
                          mb: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(39, 106, 120, 0.2)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(39, 106, 120, 0.5)',
                          },
                        }}
                      />
                    )}
                  </Box>
                )}




              </Grid>

              {/* Right Column - Letter Preview */}
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      gap: 1,
                    }}
                  >
                    <FaEnvelope size={16} color="#276a78" />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: '#276a78',
                      }}
                    >
                      Letter Preview
                    </Typography>
                  </Box>
                  
                  <TextField
                    multiline
                    rows={isMobile ? 10 : 20}
                    fullWidth
                    value={editableTemplate}
                    onChange={(e) => setEditableTemplate(e.target.value)}
                    sx={{ 
                      flexGrow: 1,
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-root': {
                        height: '100%',
                        borderRadius: '12px',
                        borderColor: 'rgba(39, 106, 120, 0.2)',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(39, 106, 120, 0.5)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#276a78',
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        fontFamily: '"Poppins", sans-serif',
                      }
                    }}
                    placeholder="Letter content will appear here..."
                    variant="outlined"
                  />
                </Box>
              </Grid>
            </Grid>
          <EmailSendingAnimation isVisible={showAnimation} />
          </DialogContent>

          <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.05)' }} />

          <DialogActions sx={{ p: 3, justifyContent: "space-between", bgcolor: 'rgba(39, 106, 120, 0.03)' }}>
            <Button 
              onClick={handleCloseModal} 
              color="inherit"
              sx={{ 
                borderRadius: '8px',
                color: '#64748b', 
                px: 3, 
                '&:hover': {
                  bgcolor: 'rgba(100, 116, 139, 0.08)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={handleSendEmail}
              disabled={!selectedEmployee || !editableTemplate || isSending}
              startIcon={
                isSending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <FaEnvelope size={16} />
                )
              }
              sx={{
                bgcolor: '#276a78',
                borderRadius: '8px',
                px: 3,
                py: 1,
                fontWeight: 600,
                textTransform: 'none',
                fontFamily: 'Poppins',
                '&:hover': {
                  bgcolor: '#1d5761',
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(39, 106, 120, 0.3)',
                  color: 'rgba(255, 255, 255, 0.6)'
                }
              }}
            >
              {isSending ? "Sending..." : "Send Email"}
            </Button>
          </DialogActions>
        </Dialog>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default LettersPage;
